/**
 * Unified "click the BRANDCENTRAL logo" behavior.
 *
 * Wherever the brand mark appears (PillNav on the home page, PageLayout
 * floating logo on every sub-page, Footer logo + wordmark on every page),
 * clicking it should land the user at the hero section of the home page
 * — full stop. This helper centralizes that behavior so the three call
 * sites can't drift apart.
 *
 * Behavior:
 *   - If we're already on `/`, smooth-scroll to position 0 (the hero).
 *   - If we're on a different route, set a sessionStorage hint so the
 *     HomePage mount-effect can scroll-to-top after the route lands,
 *     then navigate to `/`.
 *
 * The sessionStorage hint key (`bc.pendingScroll`) is shared with the
 * existing anchor-scroll system used by PillNav / Footer's "Services"
 * button. A value of `'top'` is the sentinel for hero/scroll-to-zero;
 * any other value is treated as a CSS selector to scroll to.
 */

type NavigateFn = (to: string) => void;

const PENDING_KEY = 'bc.pendingScroll';
const TOP_SENTINEL = 'top';

export const goHome = (pathname: string, navigate: NavigateFn): void => {
  // Already on the home route — just scroll to the hero (native smooth).
  if (pathname === '/') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  // On a sub-page — leave a breadcrumb for HomePage's mount-effect, then
  // navigate. HomePage reads `bc.pendingScroll` on mount; when it sees the
  // `'top'` sentinel it scrolls to position 0 rather than treating the
  // value as a CSS selector.
  try {
    window.sessionStorage.setItem(PENDING_KEY, TOP_SENTINEL);
  } catch {
    /* swallow Safari-private / quota */
  }
  navigate('/');
};

export const PENDING_SCROLL_KEY = PENDING_KEY;
export const TOP_SCROLL_SENTINEL = TOP_SENTINEL;
